/**
 * Event Edit Modal — Redesigned
 *
 * Three-zone layout:
 *   Zone 1 — Identity (title)
 *   Zone 2 — Time (start/end, all-day toggle, duration preservation)
 *   Zone 3 — Enhancers (type, location, description, notes — collapsed by default)
 *
 * Core rules:
 *   - End is never optional. Every event has start + end.
 *   - All-day is a mode toggle, not a type.
 *   - Changing start preserves duration. Changing end changes duration.
 *   - The modal is always in a valid state (start < end).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  NAMED_PRESETS,
  type NamedPreset,
  stripRrulePrefix,
  buildPresetRrule,
  classifyRrulePreset,
  parseCustomRecurrence,
} from './eventRecurrenceRules';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useClock24Hour } from '@/common/hooks/useClock24Hour';
import { TaskTimePicker } from '../tasks/components/TaskTimePicker';
import { useSession } from '@/features/auth/hooks/useSession';
import { AttendeeChips } from './AttendeeChips';
import { VideoConferenceField } from './VideoConferenceField';
import { LocationAutocompleteField } from '../places/components/LocationAutocompleteField';
import { LocationLink } from '../places/components/LocationLink';
import { buildMapUrl } from '../places/lib/mapUrl';
import { formatTimeForInput } from './dateTimeUtils';
import { UnsavedChangesGuardModal } from '@/common/components/UnsavedChangesGuardModal';
import './EventEditModal.css';

interface EventNote {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  updatedAt?: string;
}

interface EventObject {
  id: string;
  type: 'event';
  title: string;
  startAt: string;
  endAt?: string;
  allDay?: boolean;
  timezone: string;
  location?: string;
  description?: string;
  eventType?: 'activity' | 'call' | 'flight' | 'train' | 'hotel' | 'restaurant' | 'meeting' | 'birthday' | 'workout';
  notes?: EventNote[];
  rrule?: string;
  recurringEventId?: string;
  originalStartAt?: string;
  _etag?: string;
  version?: number;
  authorName?: string;
  createdAt?: string;
  providerCalendarId?: string;
  providerCalendarName?: string;
  providerEventId?: string;
  // Outbound-push sync state (#3951): 'error' => the last push to the provider
  // calendar failed (providerSyncError holds the message). Drives the modal's
  // "not synced to Google" banner + Retry button.
  providerSyncState?: 'synced' | 'error';
  providerSyncError?: string;
  metadata?: Record<string, any>;
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus?: string;
    photoUrl?: string;
    organizer?: boolean;
    self?: boolean;
  }>;
  hangoutLink?: string;
  customConferenceUrl?: string;
}

/** A writable calendar option (any provider) for calendar reassignment. */
interface CalendarOption {
  id: string;
  name: string;
  primary: boolean;
  accessRole: string;
}

interface EventEditModalProps {
  event?: EventObject | null;
  isNew?: boolean;
  viewOnly?: boolean;
  onClose: () => void;
  onSave: (data: Partial<EventObject>) => void;
  onDelete?: (id: string) => void;
  onArchive?: (id: string) => void;
  /** #3952: this event was flagged as a same-slot suspected duplicate. */
  isSuspectedDuplicate?: boolean;
  /** #3952: one-click resolve — delete the redundant copy of the duplicate pair
   *  (keep-one / delete-the-other via the existing delete path; the parent picks
   *  WHICH copy using the documented heuristic). Closes the modal afterward. */
  onResolveDuplicate?: () => void;
  /** Writable calendars (owner/writer, any provider) for calendar reassignment */
  calendars?: CalendarOption[];
  /** Called when user moves event to a different calendar */
  onMoveCalendar?: (eventId: string, sourceCalendarId: string, destinationCalendarId: string) => Promise<void>;
  /** #3951: retry the failed outbound push for this event. Resolves to the
   *  re-stamped sync state ('synced' on success, 'error' if it failed again) so
   *  the banner can reflect the result without closing the modal. */
  onRetrySync?: (eventId: string) => Promise<'synced' | 'error' | undefined>;
}

const EVENT_TYPES = [
  { value: 'activity', label: 'Activity', icon: '🎯' },
  { value: 'meeting', label: 'Meeting', icon: '📅' },
  { value: 'call', label: 'Call', icon: '📞' },
  { value: 'flight', label: 'Flight', icon: '✈️' },
  { value: 'train', label: 'Train', icon: '🚆' },
  { value: 'hotel', label: 'Hotel', icon: '🏨' },
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'birthday', label: 'Birthday', icon: '🎂' },
  { value: 'workout', label: 'Workout', icon: '💪' },
] as const;

const DEFAULT_DURATION_MINUTES = 60;

const COLOR_PRESETS = [
  { name: 'Tomato', color: '#D50000' },
  { name: 'Flamingo', color: '#E67C73' },
  { name: 'Tangerine', color: '#F4511E' },
  { name: 'Sage', color: '#33B679' },
  { name: 'Basil', color: '#0B8043' },
  { name: 'Peacock', color: '#039BE5' },
  { name: 'Blueberry', color: '#3F51B5' },
  { name: 'Lavender', color: '#7986CB' },
  { name: 'Grape', color: '#8E24AA' },
  { name: 'Graphite', color: '#616161' },
] as const;

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDateForInput(isoString: string): string {
  if (!isoString) return '';
  // Handle date-only strings (YYYY-MM-DD) without timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) return isoString;
  const d = new Date(isoString);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function hasSpecificTime(isoString: string): boolean {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) return false;
  const d = new Date(isoString);
  return !(d.getHours() === 0 && d.getMinutes() === 0);
}

function roundToNearest15(): string {
  const now = new Date();
  const min = Math.ceil(now.getMinutes() / 15) * 15;
  now.setMinutes(min, 0, 0);
  if (min === 60) {
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
  }
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function todayDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse "YYYY-MM-DD" + "HH:MM" into a Date (local timezone). */
function parseDateTime(dateStr: string, timeStr: string): Date {
  return new Date(`${dateStr}T${timeStr}:00`);
}

/** Add minutes to a Date and return a new Date. */
function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

/** Difference in minutes between two Dates. */
function diffMinutes(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Component ────────────────────────────────────────────────────────────

export function EventEditModal({ event, isNew, viewOnly = false, onClose, onSave, onDelete, onArchive, isSuspectedDuplicate = false, onResolveDuplicate, calendars, onMoveCalendar, onRetrySync }: EventEditModalProps) {
  // Unsaved-changes guard (escape-layer contract, issue #3938). isDirtyRef is set
  // by markDirty (wired to the edit form's onInput/onChange below); handleSafeClose
  // routes Esc / X / backdrop through the confirmation when dirty instead of
  // discarding silently (the bottom "Cancel" button stays an explicit discard).
  // useEscapeKey already preventDefaults, so the module-pane Esc handler declines
  // and the Calendar pane survives this keypress.
  const isDirtyRef = useRef(false);
  const markDirty = () => { isDirtyRef.current = true; };
  const [showUnsavedConfirmation, setShowUnsavedConfirmation] = useState(false);
  const eventFormRef = useRef<HTMLFormElement>(null);
  const handleSafeClose = useCallback(() => {
    if (isDirtyRef.current) {
      setShowUnsavedConfirmation(true);
      return;
    }
    onClose();
  }, [onClose]);
  useEscapeKey(handleSafeClose);
  const [eventModalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(handleSafeClose, 30);
  const { profile } = useSession();
  const [use24h] = useClock24Hour();
  const titleRef = useRef<HTMLInputElement>(null);

  // Hide mobile header/footer when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => { document.body.classList.remove('modal-open'); };
  }, []);

  // ── State ──────────────────────────────────────────────────────────────

  const [title, setTitle] = useState('');

  // Zone 2 — Time
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_DURATION_MINUTES);
  // Remember times before toggling all-day ON so we can restore them
  const savedTimesRef = useRef({ start: '', end: '' });
  // Whether the event opened WITH attendees. Drives the clear-all serialization
  // below: removing every attendee must send `attendees: []` (an intentional
  // clear) rather than `undefined` (omit/preserve), but only when there were
  // attendees to remove — sending `[]` on an event that never had any would let
  // the backend's genuinely-empty-`[]` clear path wipe provider-side attendees
  // the local copy hasn't synced yet (the #4073 clobber). Seeded per `event` in
  // the init effect, so a reused modal instance reflects the current event. (#4999)
  const originalHadAttendeesRef = useRef(false);

  // Zone 3 — Enhancers
  const [eventType, setEventType] = useState<string>('activity');
  const [location, setLocation] = useState('');
  // Google placeId for the selected location (empty when the user typed free text).
  const [locationPlaceId, setLocationPlaceId] = useState('');
  const [description, setDescription] = useState('');
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [customColor, setCustomColor] = useState<string | null>(null);

  // Attendees & conferencing
  const [attendees, setAttendees] = useState<NonNullable<EventObject['attendees']>>([]);
  const [sendInvitations, setSendInvitations] = useState(true);
  const [createGoogleMeet, setCreateGoogleMeet] = useState(false);
  const [customConferenceUrl, setCustomConferenceUrl] = useState('');

  // Recurrence
  const [recurrencePreset, setRecurrencePreset] = useState<string>('none');
  const [rruleString, setRruleString] = useState('');
  const [customInterval, setCustomInterval] = useState(1);
  const [customFreq, setCustomFreq] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'>('WEEKLY');
  const [customDays, setCustomDays] = useState<number[]>([new Date().getDay()]);
  const [customEndType, setCustomEndType] = useState<'never' | 'count' | 'until'>('never');
  const [customCount, setCustomCount] = useState(10);
  const [customUntil, setCustomUntil] = useState('');
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  // #4415: the builder effect must NOT overwrite the rrule seeded from an
  // existing event until the user actually touches a recurrence control —
  // otherwise opening + saving an unchanged Google master (e.g. FREQ=DAILY,
  // possibly "RRULE:"-prefixed / UNTIL-bearing) re-emits buildRrule()'s
  // WEEKLY/today default and corrupts the series. markRecurrenceDirty (wired to
  // every Repeat/custom control) flips this on; the seed effect resets it.
  const recurrenceDirtyRef = useRef(false);
  const markRecurrenceDirty = () => { recurrenceDirtyRef.current = true; };
  // #4415: restore the custom-recurrence controls to their initial defaults.
  // Used by the seed effect whenever an event has no rule (or is brand-new) so a
  // reused modal instance never carries a previously edited event's custom rule
  // into a fresh 'Custom' selection. Mirrors the useState initializers above.
  //
  // `seedDayWeekday` seeds the WEEKLY day-button selection (#4415, review fix 2):
  // for an EXISTING non-custom event, pass the event's START-DATE weekday so that
  // switching its Repeat to 'Custom' pre-selects the event's OWN day, not today.
  // Omit it (new event / no rule) to fall back to today's weekday — the original
  // default. Accepts a JS getDay() index (Sun=0..Sat=6).
  const resetCustomRecurrenceState = (seedDayWeekday?: number) => {
    setCustomFreq('WEEKLY');
    setCustomInterval(1);
    setCustomDays([seedDayWeekday != null ? seedDayWeekday : new Date().getDay()]);
    setCustomEndType('never');
    setCustomCount(10);
    setCustomUntil('');
  };

  // Notes
  const [notes, setNotes] = useState<EventNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Calendar reassignment (any provider)
  const isProviderSynced = !!(event?.providerCalendarId && event?.providerEventId);
  const writableCalendars = (calendars || []).filter(
    cal => cal.accessRole === 'owner' || cal.accessRole === 'writer'
  );
  const defaultCalendarId = event?.providerCalendarId
    || writableCalendars.find(c => c.primary)?.id
    || writableCalendars[0]?.id
    || '';
  const [selectedCalendarId, setSelectedCalendarId] = useState(defaultCalendarId);
  const [isMovingCalendar, setIsMovingCalendar] = useState(false);
  const [showRecurringMoveConfirm, setShowRecurringMoveConfirm] = useState(false);
  const [pendingMoveCalendarId, setPendingMoveCalendarId] = useState('');

  // #3951: outbound-push sync state. 'error' => the last push to the provider
  // calendar failed; surface a "not synced to Google" banner + Retry button.
  // `syncState` is local so a successful retry can clear the banner in place
  // (the parent's invalidation also refetches, but in-place keeps the modal
  // honest if it stays open).
  const [syncState, setSyncState] = useState<'synced' | 'error' | undefined>(undefined);
  const [isRetryingSync, setIsRetryingSync] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  useEffect(() => {
    setSyncState(event?.providerSyncState);
    setRetryFailed(false);
  }, [event?.id, event?.providerSyncState]);
  const handleRetrySync = async () => {
    if (!event?.id || !onRetrySync || isRetryingSync) return;
    setIsRetryingSync(true);
    setRetryFailed(false);
    try {
      const next = await onRetrySync(event.id);
      setSyncState(next);
      if (next !== 'synced') setRetryFailed(true);
    } catch {
      setRetryFailed(true);
    } finally {
      setIsRetryingSync(false);
    }
  };

  // Sync selectedCalendarId when calendars load asynchronously (useState only uses initial value on mount)
  useEffect(() => {
    if (!selectedCalendarId && writableCalendars.length > 0) {
      const fallback = writableCalendars.find(c => c.primary)?.id || writableCalendars[0]?.id || '';
      if (fallback) setSelectedCalendarId(fallback);
    }
  }, [writableCalendars, selectedCalendarId]);

  // ── Initialization ─────────────────────────────────────────────────────

  useEffect(() => {
    if (event) {
      // Editing existing event
      setTitle(event.title || '');
      setStartDate(formatDateForInput(event.startAt));

      const isAllDay = event.allDay || !hasSpecificTime(event.startAt);
      setAllDay(isAllDay);

      if (isAllDay) {
        setStartTime('09:00');
        savedTimesRef.current = { start: '09:00', end: '10:00' };
      } else {
        const st = formatTimeForInput(event.startAt);
        setStartTime(st);
        savedTimesRef.current.start = st;
      }

      if (event.endAt) {
        setEndDate(formatDateForInput(event.endAt));
        if (!isAllDay && hasSpecificTime(event.endAt)) {
          const et = formatTimeForInput(event.endAt);
          setEndTime(et);
          savedTimesRef.current.end = et;
        } else {
          // Compute a sensible end time from start + 1h
          const startDt = new Date(event.startAt);
          const endDt = addMinutes(startDt, DEFAULT_DURATION_MINUTES);
          const et = formatTime(endDt);
          setEndTime(et);
          savedTimesRef.current.end = et;
        }
        // Compute actual duration
        const dur = diffMinutes(new Date(event.startAt), new Date(event.endAt));
        setDurationMinutes(dur > 0 ? dur : DEFAULT_DURATION_MINUTES);
      } else {
        // Legacy event with no end — synthesize end = start + 1h
        setEndDate(formatDateForInput(event.startAt));
        const startDt = new Date(event.startAt);
        const endDt = addMinutes(startDt, DEFAULT_DURATION_MINUTES);
        const et = formatTime(endDt);
        setEndTime(et);
        savedTimesRef.current.end = et;
        setDurationMinutes(DEFAULT_DURATION_MINUTES);
      }

      setEventType(event.eventType || 'activity');
      setLocation(event.location || '');
      setLocationPlaceId(event.metadata?.locationPlaceId || '');
      setDescription(event.description || '');
      setCustomColor(event.metadata?.customColor || null);
      // Seed UNCONDITIONALLY — this modal instance is reused across events, so an
      // attendee-less event MUST clear the prior event's attendee state to []
      // (#5051). With no else, swapping from an event that HAD attendees left the
      // stale list in state, which serialized onto the attendee-less event on save.
      setAttendees(event.attendees ?? []);
      originalHadAttendeesRef.current = (event.attendees?.length ?? 0) > 0;
      if (event.customConferenceUrl) setCustomConferenceUrl(event.customConferenceUrl);

      // Recurrence (#4415: identity-preserving seed). Strip a leading "RRULE:"
      // (Google sync's wire form) before classifying. Any UNTIL/COUNT/INTERVAL>1
      // -bearing rule classifies as 'custom' (a bare preset's builder can't
      // reproduce it), and parseCustomRecurrence seeds the custom controls so the
      // rule round-trips faithfully. The builder effect is guarded
      // (recurrenceDirtyRef) so this seeded rrule survives an unchanged save
      // unmodified — see below.
      recurrenceDirtyRef.current = false;
      // #4415 (review fix 2): when resetting the custom controls on behalf of an
      // EXISTING event, seed the WEEKLY day-button selection from the event's OWN
      // start weekday (not today) so switching its Repeat to 'Custom' pre-selects
      // the event's day. Derive from event.startAt — the same start the builder
      // anchors on — since state `startDate` isn't observable this render pass.
      const seedStartDate = formatDateForInput(event.startAt);
      const startWeekday = seedStartDate && seedStartDate.trim()
        ? new Date(seedStartDate + 'T00:00:00').getDay()
        : undefined;
      if (event.rrule) {
        setRruleString(event.rrule);
        const body = stripRrulePrefix(event.rrule);
        // Classify by ROUND-TRIP against the event's OWN start context (the same
        // `startDate` value the builder reads once committed) (#4415).
        const preset = classifyRrulePreset(event.rrule, seedStartDate);
        setRecurrencePreset(preset);
        // When a non-canonical rule lands in the 'custom' bucket, seed the
        // custom controls from it (inverse of buildRrule) so the Custom UI
        // reflects the event's real rule instead of WEEKLY/today defaults.
        if (preset === 'custom') {
          const custom = parseCustomRecurrence(body);
          if (custom) {
            setCustomFreq(custom.customFreq);
            setCustomInterval(custom.customInterval);
            // Seed customDays UNCONDITIONALLY — when the parsed rule has no
            // byweekday (custom.customDays empty, e.g. a DAILY/MONTHLY custom or
            // a malformed body), clear to [] so stale MO,WE days from a PRIOR
            // weekly-custom event in this reused modal can't survive (#4415,
            // review fix 5). A non-empty set replaces them outright.
            setCustomDays(custom.customDays.length > 0 ? custom.customDays : []);
            setCustomEndType(custom.customEndType);
            setCustomCount(custom.customCount);
            setCustomUntil(custom.customUntil);
          } else {
            // parseCustomRecurrence returned null (RRule.parseString threw on a
            // malformed/unrecognized rule). The custom controls were NOT seeded,
            // so a reused modal would otherwise keep a prior event's custom state.
            // Reset to clean defaults so a null parse never leaks stale controls
            // (#4415, review fix 3). Seed the event's own start weekday (fix 2).
            resetCustomRecurrenceState(startWeekday);
          }
        } else {
          // A NON-custom preset (daily/weekly/weekdays/monthly/yearly) does not
          // seed the custom controls. This modal instance can be reused — if the
          // PRIOR event was custom, its parsed customFreq/customInterval/customDays/
          // customUntil would survive here and silently pre-populate this event's
          // 'Custom' selection if the user switches the Repeat to Custom. Reset to
          // defaults so a non-custom event always opens Custom clean — seeding this
          // event's own start weekday so 'Custom' pre-selects its day (#4415, fix 2).
          resetCustomRecurrenceState(startWeekday);
        }
      } else {
        // Event present but no rule. Reset the rrule + preset AND the custom
        // controls — this modal instance can be reused (selectedEvent swapped
        // from a custom recurring event to a non-recurring one) and stale custom
        // state would otherwise pre-populate a fresh 'Custom' selection (#4415).
        // Seed this event's own start weekday so 'Custom' pre-selects its day (fix 2).
        setRruleString('');
        setRecurrencePreset('none');
        resetCustomRecurrenceState(startWeekday);
      }

      // Expand details if any enhancer fields have values
      if (event.rrule || event.location || event.description || (event.eventType && event.eventType !== 'activity') || event.metadata?.customColor) {
        setDetailsExpanded(true);
      }

      // Migrate legacy notes
      const migratedNotes = (event.notes || []).map((note: any) => ({
        id: note.id,
        content: note.content || note.note || '',
        authorId: note.authorId || profile?.id || '',
        authorName: note.authorName || profile?.name || 'Unknown',
        createdAt: note.createdAt || note.date || new Date().toISOString(),
        updatedAt: note.updatedAt,
      }));
      setNotes(migratedNotes);
    } else {
      // New event — sensible defaults
      const now = roundToNearest15();
      const startDt = parseDateTime(todayDateString(), now);
      const endDt = addMinutes(startDt, DEFAULT_DURATION_MINUTES);

      setTitle('');
      setStartDate(todayDateString());
      setStartTime(now);
      setEndDate(formatDate(endDt));
      setEndTime(formatTime(endDt));
      setAllDay(false);
      setDurationMinutes(DEFAULT_DURATION_MINUTES);
      savedTimesRef.current = { start: now, end: formatTime(endDt) };

      setEventType('activity');
      setLocation('');
      setLocationPlaceId('');
      setDescription('');
      setDetailsExpanded(false);
      setNotes([]);
      // Reset attendee state too — a reused modal swapping from an event that HAD
      // attendees to a fresh (attendee-less) event must not carry them over (#5051).
      setAttendees([]);
      originalHadAttendeesRef.current = false;
      // #4415: a fresh event has no rule yet; the user picking a Repeat option
      // is a genuine change, so leave the builder free to emit it. Reset the
      // custom-recurrence controls too — otherwise stale values from a previously
      // edited custom event carry over and silently pre-populate a fresh rule when
      // the user later selects 'Custom'.
      setRruleString('');
      setRecurrencePreset('none');
      resetCustomRecurrenceState();
      recurrenceDirtyRef.current = true;
    }
    setNewNote('');
    setEditingNoteId(null);
    setEditingNoteText('');
  }, [event, profile]);

  // Auto-focus title after mount
  useEffect(() => {
    if (!viewOnly) {
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [viewOnly]);

  // ── Recurrence RRULE builder ───────────────────────────────────────────

  const buildRrule = useCallback(() => {
    if (recurrencePreset === 'none') return '';
    // Named presets delegate to the SAME pure builder the round-trip classifier
    // compares against, so classification and re-emission can never diverge (#4415).
    if ((NAMED_PRESETS as readonly string[]).includes(recurrencePreset)) {
      return buildPresetRrule(recurrencePreset as NamedPreset, startDate);
    }
    // Custom
    const parts = [`FREQ=${customFreq}`];
    if (customInterval > 1) parts.push(`INTERVAL=${customInterval}`);
    if (customFreq === 'WEEKLY' && customDays.length > 0) {
      const dayStrs = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      parts.push(`BYDAY=${customDays.sort().map(d => dayStrs[d]).join(',')}`);
    }
    if (customEndType === 'count') parts.push(`COUNT=${customCount}`);
    if (customEndType === 'until' && customUntil) {
      // #4415 (review finding 3 — INTENTIONAL): the custom controls model UNTIL as
      // a DATE only (an <input type="date">, no time-of-day field), so the builder
      // canonicalizes it to END-OF-DAY UTC inclusive (T235959Z). RFC 5545 UNTIL is
      // inclusive, and a date-only bound means "through the end of that day" — so a
      // Google source emitting midnight UNTIL=…T000000Z is normalized UP to end-of-
      // day here, NOT shifted down a day. This canonicalization only fires when the
      // user actually edits a recurrence control; an UNTOUCHED event round-trips its
      // original UNTIL verbatim via the recurrenceDirtyRef guard (the rruleString
      // seeded from event.rrule is preserved byte-for-byte). Preserving the original
      // time-of-day across an edit isn't feasible without a time-of-day control the
      // date-only model lacks — and end-of-day-inclusive is the correct reading of a
      // date bound, so we standardize on it deliberately rather than truncate the
      // final day.
      const u = customUntil.replace(/-/g, '') + 'T235959Z';
      parts.push(`UNTIL=${u}`);
    }
    return parts.join(';');
  }, [recurrencePreset, startDate, customFreq, customInterval, customDays, customEndType, customCount, customUntil]);

  useEffect(() => {
    // #4415: don't clobber the rrule seeded from an existing event until the
    // user actually changes a recurrence control. Without this guard the
    // builder runs on mount and rewrites a Google FREQ=DAILY (or any rule we
    // round-tripped) into buildRrule()'s WEEKLY/today default on the first
    // unchanged save. markRecurrenceDirty (wired to the Repeat select + every
    // custom control) flips the gate the moment the user edits recurrence.
    if (!recurrenceDirtyRef.current) return;
    setRruleString(buildRrule());
  }, [buildRrule]);

  // ── Duration Preservation ──────────────────────────────────────────────

  const handleStartDateChange = useCallback((newDate: string) => {
    setStartDate(newDate);
    // #4415 (review fix 1): the named-preset builder derives its anchor from the
    // start date — buildPresetRrule('weekly') emits BYDAY=<start weekday> and
    // ('monthly') emits BYMONTHDAY=<start day>. The recurrenceDirtyRef guard was
    // added to stop the on-mount rewrite, but it also froze this LEGITIMATE
    // update: moving a weekly event Mon→Wed left the stale BYDAY=MO. Mark the
    // recurrence dirty so the builder re-emits the correct anchor on save. Safe
    // for 'custom' (buildRrule's custom branch uses the explicit customDays/
    // customUntil — never startDate — so re-emitting yields the SAME custom rule)
    // and for 'none' (buildRrule returns '').
    markRecurrenceDirty();
    if (allDay) {
      // For all-day: preserve the multi-day span by shifting end by the same
      // whole-day delta the start moved (#4372). Without this, dragging the
      // start date earlier/later left end fixed, corrupting the span (and
      // causing downstream duplication/shift). Mirrors the timed branch and the
      // dnd handler (useCalendarDnd), which both preserve duration.
      if (endDate && startDate) {
        const prevStart = new Date(startDate + 'T00:00:00');
        const nextStart = new Date(newDate + 'T00:00:00');
        // Shift by WHOLE DAYS, not raw ms: a ms delta carries the DST hour
        // (a spring-forward week is 23h, fall-back 25h), so adding it to an end
        // date on the other side of the boundary lands at 23:00/01:00 and
        // formatDate reads back the wrong calendar day (#4372). Round to days
        // and advance the end with setDate so the span stays exact across DST.
        const deltaDays = Math.round((nextStart.getTime() - prevStart.getTime()) / 86_400_000);
        if (deltaDays !== 0) {
          const shiftedEnd = new Date(endDate + 'T00:00:00');
          shiftedEnd.setDate(shiftedEnd.getDate() + deltaDays);
          setEndDate(formatDate(shiftedEnd));
          return;
        }
      }
      // Fallback guard: end must never precede the new start.
      if (endDate && newDate > endDate) {
        setEndDate(newDate);
      }
      return;
    }
    // Preserve duration: shift end by the same delta
    const newStart = parseDateTime(newDate, startTime);
    const newEnd = addMinutes(newStart, durationMinutes);
    setEndDate(formatDate(newEnd));
    setEndTime(formatTime(newEnd));
  }, [startDate, startTime, endDate, durationMinutes, allDay]);

  const handleStartTimeChange = useCallback((newTime: string) => {
    setStartTime(newTime);
    // Preserve duration: shift end
    const newStart = parseDateTime(startDate, newTime);
    const newEnd = addMinutes(newStart, durationMinutes);
    setEndDate(formatDate(newEnd));
    setEndTime(formatTime(newEnd));
  }, [startDate, durationMinutes]);

  const handleEndDateChange = useCallback((newDate: string) => {
    if (allDay) {
      // All-day: enforce end >= start
      if (newDate < startDate) {
        setEndDate(startDate);
        return;
      }
      setEndDate(newDate);
      return;
    }
    const start = parseDateTime(startDate, startTime);
    const newEnd = parseDateTime(newDate, endTime);
    if (newEnd <= start) {
      // Auto-correct: push end to start + 15 min
      const corrected = addMinutes(start, 15);
      setEndDate(formatDate(corrected));
      setEndTime(formatTime(corrected));
      setDurationMinutes(15);
    } else {
      setEndDate(newDate);
      setDurationMinutes(diffMinutes(start, newEnd));
    }
  }, [startDate, startTime, endTime, allDay]);

  const handleEndTimeChange = useCallback((newTime: string) => {
    const start = parseDateTime(startDate, startTime);
    const newEnd = parseDateTime(endDate, newTime);
    if (newEnd <= start) {
      // Auto-correct: push end to start + 15 min
      const corrected = addMinutes(start, 15);
      setEndDate(formatDate(corrected));
      setEndTime(formatTime(corrected));
      setDurationMinutes(15);
    } else {
      setEndTime(newTime);
      setDurationMinutes(diffMinutes(start, newEnd));
    }
  }, [startDate, startTime, endDate]);

  // ── All-Day Toggle ─────────────────────────────────────────────────────

  const handleAllDayToggle = useCallback(() => {
    if (!allDay) {
      // Turning ON: save current times, hide them
      savedTimesRef.current = { start: startTime, end: endTime };
      setAllDay(true);
      // Ensure end date is set and not before start date
      if (!endDate) {
        setEndDate(startDate);
      } else if (endDate < startDate) {
        setEndDate(startDate);
      }
    } else {
      // Turning OFF: restore saved times
      setAllDay(false);
      const restoredStart = savedTimesRef.current.start || roundToNearest15();
      setStartTime(restoredStart);
      const restoredEnd = savedTimesRef.current.end || formatTime(addMinutes(parseDateTime(startDate, restoredStart), DEFAULT_DURATION_MINUTES));
      setEndTime(restoredEnd);
      // Recompute duration
      const s = parseDateTime(startDate, restoredStart);
      const e = parseDateTime(endDate || startDate, restoredEnd);
      const dur = diffMinutes(s, e);
      setDurationMinutes(dur > 0 ? dur : DEFAULT_DURATION_MINUTES);
    }
  }, [allDay, startTime, endTime, startDate, endDate]);

  // ── Submit ─────────────────────────────────────────────────────────────

  // Handle calendar move (for Google-synced events)
  const handleCalendarChange = useCallback((newCalendarId: string) => {
    if (!event?.providerCalendarId || !event?.providerEventId || newCalendarId === event.providerCalendarId) {
      setSelectedCalendarId(newCalendarId);
      return;
    }

    // For recurring events, show confirmation
    if (event.rrule || event.recurringEventId) {
      setPendingMoveCalendarId(newCalendarId);
      setShowRecurringMoveConfirm(true);
      return;
    }

    setSelectedCalendarId(newCalendarId);
  }, [event]);

  const confirmRecurringMove = useCallback(() => {
    setSelectedCalendarId(pendingMoveCalendarId);
    setShowRecurringMoveConfirm(false);
    setPendingMoveCalendarId('');
  }, [pendingMoveCalendarId]);

  const cancelRecurringMove = useCallback(() => {
    setShowRecurringMoveConfirm(false);
    setPendingMoveCalendarId('');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startDate) return;

    // Handle calendar move if calendar changed
    const calendarChanged = isProviderSynced && selectedCalendarId && selectedCalendarId !== event?.providerCalendarId;
    if (calendarChanged && onMoveCalendar && event?.providerEventId && event?.providerCalendarId) {
      setIsMovingCalendar(true);
      try {
        await onMoveCalendar(event.providerEventId, event.providerCalendarId, selectedCalendarId);
      } catch (err) {
        console.error('Failed to move event between calendars:', err);
        setIsMovingCalendar(false);
        return;
      }
      setIsMovingCalendar(false);
    }

    let startAt: string;
    let endAt: string;

    if (allDay) {
      // For all-day events, use date-only strings (like Google Calendar)
      // This avoids timezone conversion issues
      startAt = startDate;
      endAt = endDate || startDate;
    } else {
      if (!endDate || !startTime || !endTime) return; // Safety check for timed events
      startAt = new Date(`${startDate}T${startTime}`).toISOString();
      endAt = new Date(`${endDate}T${endTime}`).toISOString();
    }

    const metadata: Record<string, any> = { ...(event?.metadata || {}) };
    if (customColor) {
      metadata.customColor = customColor;
    } else {
      delete metadata.customColor;
    }
    // Persist the resolved place id alongside the location text. Cleared when the
    // location is blank or was entered as free text.
    if (location.trim() && locationPlaceId) {
      metadata.locationPlaceId = locationPlaceId;
    } else {
      delete metadata.locationPlaceId;
    }

    const data: Partial<EventObject> & { sendInvitations?: boolean; createGoogleMeet?: boolean } = {
      title: title.trim(),
      startAt,
      endAt,
      allDay,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      eventType: eventType as any,
      location: location.trim() || undefined,
      description: description.trim() || undefined,
      rrule: rruleString || undefined,
      notes: notes.length > 0 ? notes : undefined,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      // Attendees & conferencing. A non-empty list is sent as-is. When the list
      // is empty, distinguish an intentional clear-ALL (the event opened WITH
      // attendees and the user removed every one — send `[]` so the backend
      // propagates the removal to the local object AND the provider) from an
      // event that simply never had attendees (send `undefined` to omit/preserve,
      // so we never spuriously clear provider-side attendees the local copy
      // hasn't synced yet). (#4999)
      attendees: attendees.length > 0 ? attendees : (originalHadAttendeesRef.current ? [] : undefined),
      customConferenceUrl: customConferenceUrl || undefined,
    };

    // Add transient flags for the API. Always transmit sendInvitations so the
    // API never has to fall back to a default — an omitted flag previously left
    // the update path to default it to false, suppressing invites on edit (#4214).
    data.sendInvitations = sendInvitations;
    if (createGoogleMeet) data.createGoogleMeet = true;

    // Include target calendar for new events
    if (isNew && selectedCalendarId) {
      data.providerCalendarId = selectedCalendarId;
    }

    onSave(data);
  };

  // ── Validation ─────────────────────────────────────────────────────────

  const isValid = (() => {
    if (!title.trim() || !startDate) return false;
    if (allDay) {
      // For all-day events, end date defaults to start date if not set
      const effectiveEndDate = endDate || startDate;
      return effectiveEndDate >= startDate;
    }
    // For timed events, need all time fields
    if (!endDate || !startTime || !endTime) return false;
    const start = parseDateTime(startDate, startTime);
    const end = parseDateTime(endDate, endTime);
    return end > start;
  })();

  // ── Note Management ────────────────────────────────────────────────────

  const handleAddNote = () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    const newNoteObj: EventNote = {
      id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: trimmed,
      authorId: profile?.id || '',
      authorName: profile?.name || 'Unknown',
      createdAt: new Date().toISOString(),
    };
    setNotes([newNoteObj, ...notes]);
    setNewNote('');
  };

  const handleDeleteNote = (id: string) => {
    const updatedNotes = notes.filter(n => n.id !== id);
    setNotes(updatedNotes);
    if (!isNew && event) {
      onSave({ notes: updatedNotes.length > 0 ? updatedNotes : undefined });
    }
  };

  const handleEditNote = (noteObj: EventNote) => {
    setEditingNoteId(noteObj.id);
    setEditingNoteText(noteObj.content);
  };

  const handleSaveNoteEdit = () => {
    if (!editingNoteId) return;
    const trimmed = editingNoteText.trim();
    if (!trimmed) {
      handleDeleteNote(editingNoteId);
    } else {
      const updatedNotes = notes.map(n =>
        n.id === editingNoteId ? { ...n, content: trimmed, updatedAt: new Date().toISOString() } : n
      );
      setNotes(updatedNotes);
      if (!isNew && event) {
        onSave({ notes: updatedNotes });
      }
    }
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const modalContent = (
    <div
      className="event-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        pointerEvents: 'auto',
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={eventModalContentRef}
        className="event-edit-modal-container"
        // borderRadius: 16 matches the rest of this modal's inline styling;
        // the rest (size, layout, max-height) lives in EventEditModal.css
        // so responsive @media + the 15vh-from-bottom cap can take effect.
        style={{ borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {viewOnly ? (title || 'Untitled Event') : isNew ? 'New Event' : 'Edit Event'}
          </h2>
          <button
            onClick={handleSafeClose}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44, background: '#f3f4f6', border: 'none',
              borderRadius: '50%', color: 'var(--color-text-muted)', fontSize: 28, lineHeight: 1,
              cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
          >
            ×
          </button>
        </div>

        <div className="event-modal-body">
          {/* ── View Only Mode ──────────────────────────────────────────── */}
          {viewOnly ? (
            <div className="event-view-content">
              {event?.startAt && (
                <div className="event-view-row">
                  <span className="event-view-label">When</span>
                  <span className="event-view-value">
                    {event.allDay || !hasSpecificTime(event.startAt)
                      ? new Date(event.startAt + (event.startAt.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                      : `${new Date(event.startAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at ${new Date(event.startAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                    }
                    {event.endAt && (
                      <>
                        {' → '}
                        {event.allDay || !hasSpecificTime(event.endAt)
                          ? new Date(event.endAt + (event.endAt.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                          : (
                            <>
                              {new Date(event.endAt).toLocaleDateString('en-US') !== new Date(event.startAt).toLocaleDateString('en-US')
                                ? `${new Date(event.endAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} `
                                : ''}
                              {new Date(event.endAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </>
                          )
                        }
                      </>
                    )}
                    {(event.allDay || !hasSpecificTime(event.startAt)) && ' (all day)'}
                  </span>
                </div>
              )}

              <div className="event-view-row">
                <span className="event-view-label">Type</span>
                <span className="event-view-value">
                  {EVENT_TYPES.find(t => t.value === (event?.eventType || eventType))?.icon}{' '}
                  {EVENT_TYPES.find(t => t.value === (event?.eventType || eventType))?.label || eventType}
                </span>
              </div>

              {(event?.location || location) && (
                <div className="event-view-row">
                  <span className="event-view-label">Location</span>
                  <span className="event-view-value">
                    <LocationLink
                      location={event?.location || location}
                      placeId={event?.metadata?.locationPlaceId || locationPlaceId}
                    />
                  </span>
                </div>
              )}

              <div className="event-view-meta">
                <div className="event-view-meta-row">
                  <span className="event-view-meta-label">Created by</span>
                  <span>{event?.authorName || 'Unknown'}</span>
                </div>
                <div className="event-view-meta-row">
                  <span className="event-view-meta-label">Created on</span>
                  <span>
                    {event?.createdAt
                      ? `${new Date(event.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} at ${new Date(event.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                      : 'Unknown'}
                  </span>
                </div>
              </div>

              {description && (
                <div className="event-notes-section">
                  <div className="event-notes-title">Description</div>
                  <p className="event-note-content" style={{ whiteSpace: 'pre-wrap' }}>{description}</p>
                </div>
              )}

              {notes.length > 0 && (
                <div className="event-notes-section">
                  <div className="event-notes-title">Notes</div>
                  {notes.map((noteObj) => (
                    <div key={noteObj.id} className="event-note-item">
                      <div className="event-note-header">
                        <span className="event-note-meta">
                          {noteObj.authorName || 'Unknown'} — {new Date(noteObj.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {new Date(noteObj.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          {noteObj.updatedAt && noteObj.updatedAt !== noteObj.createdAt && ' (edited)'}
                        </span>
                      </div>
                      <p className="event-note-content">{noteObj.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (

          /* ── Edit Mode: Three Zones ─────────────────────────────────── */
          <form ref={eventFormRef} onSubmit={handleSubmit} onInput={markDirty} onChange={markDirty} className="event-modal-form">

            {/* ═══ Zone 1 — Identity ═══ */}
            <div className="event-field">
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="dinner with susan 7pm pastis"
                className="event-field-input event-title-input"
                required
              />
            </div>

            {/* #3952: suspected-duplicate banner with a one-click resolve. Shown
                only for an EXISTING event the parent flagged (same time slot +
                fuzzy-similar title on the same account). Resolve deletes the
                redundant copy via the existing delete path — flag-first and
                user-initiated, never auto-merge (legit same-time events exist). */}
            {!isNew && isSuspectedDuplicate && onResolveDuplicate && (
              <div className="event-dup-banner" role="note">
                <span className="event-dup-banner-icon" aria-hidden>⚠</span>
                <span className="event-dup-banner-text">
                  Possible duplicate — another event shares this exact time slot with a similar title.
                </span>
                <button
                  type="button"
                  className="event-dup-banner-btn"
                  onClick={() => {
                    if (confirm('Delete the duplicate copy of this event? The matching event stays on your calendar.')) {
                      onResolveDuplicate();
                      onClose();
                    }
                  }}
                >
                  Resolve
                </button>
              </div>
            )}

            {/* #3951: "not synced to Google" banner with a Retry affordance.
                Shown for an EXISTING event whose last outbound push failed
                (providerSyncState === 'error'). Retry re-runs the push; on
                success the banner clears in place, otherwise it reports the
                repeated failure. The error message comes from providerSyncError. */}
            {!isNew && syncState === 'error' && (
              <div className="event-sync-error-banner" role="alert">
                <span className="event-sync-error-banner-icon" aria-hidden>⚠</span>
                <span className="event-sync-error-banner-text">
                  {isRetryingSync
                    ? 'Retrying sync to Google…'
                    : retryFailed
                      ? `Still not synced to Google${event?.providerSyncError ? ` — ${event.providerSyncError}` : ''}`
                      : `Not synced to Google${event?.providerSyncError ? ` — ${event.providerSyncError}` : ''}`}
                </span>
                {onRetrySync && (
                  <button
                    type="button"
                    className="event-sync-error-banner-btn"
                    onClick={handleRetrySync}
                    disabled={isRetryingSync}
                  >
                    {isRetryingSync ? 'Retrying…' : 'Retry sync'}
                  </button>
                )}
              </div>
            )}

            {/* Google Calendar picker — prominent for new events */}
            {isNew && writableCalendars.length > 1 && (
              <div className="event-field">
                <label className="event-field-label">Calendar</label>
                <select
                  value={selectedCalendarId}
                  onChange={(e) => setSelectedCalendarId(e.target.value)}
                  className="event-field-input"
                >
                  {writableCalendars.map((cal) => (
                    <option key={cal.id} value={cal.id}>
                      {cal.name}{cal.primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ═══ Zone 2 — Time ═══ */}
            <div className="event-time-zone">
              {/* All-day toggle */}
              <label className="event-allday-toggle">
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={handleAllDayToggle}
                />
                <span className="event-allday-label">All day</span>
              </label>

              {/* Start row */}
              <div className="event-time-row">
                <span className="event-time-row-label">Start</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="event-field-input event-date-input"
                  required
                />
                {!allDay && (
                  <TaskTimePicker
                    value={startTime}
                    onChange={handleStartTimeChange}
                    use24h={use24h}
                    minuteStep={5}
                    allowEmpty={false}
                  />
                )}
              </div>

              {/* End row */}
              <div className="event-time-row">
                <span className="event-time-row-label">End</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  className="event-field-input event-date-input"
                  required
                />
                {!allDay && (
                  <TaskTimePicker
                    value={endTime}
                    onChange={handleEndTimeChange}
                    use24h={use24h}
                    minuteStep={5}
                    allowEmpty={false}
                  />
                )}
              </div>
            </div>

            {/* ═══ Zone 3 — Enhancers ═══ */}
            <div className="event-enhancers-zone">
              <button
                type="button"
                className="event-details-toggle"
                onClick={() => setDetailsExpanded(!detailsExpanded)}
              >
                <span className="event-details-arrow">{detailsExpanded ? '▾' : '▸'}</span>
                Details
              </button>

              {detailsExpanded && (
                <div className="event-details-content">
                  {/* Event Type */}
                  <div className="event-field">
                    <label className="event-field-label">Type</label>
                    <select
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      className="event-field-input"
                    >
                      {EVENT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Color — only for Xavior-created events (no providerCalendarId) */}
                  {!event?.providerCalendarId && (
                    <div className="event-field">
                      <label className="event-field-label">Color</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {COLOR_PRESETS.map((preset) => (
                          <button
                            key={preset.color}
                            type="button"
                            title={preset.name}
                            onClick={() => setCustomColor(preset.color)}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: preset.color,
                              border: customColor === preset.color ? '2px solid #111827' : '2px solid transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              flexShrink: 0,
                              boxShadow: customColor === preset.color ? '0 0 0 2px white, 0 0 0 4px #111827' : 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                            }}
                          >
                            {customColor === preset.color && (
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        ))}
                        {customColor && (
                          <button
                            type="button"
                            title="Reset to default"
                            onClick={() => setCustomColor(null)}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              flexShrink: 0,
                              fontSize: 12,
                              color: 'var(--color-text-faint)',
                              fontWeight: 600,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Google Calendar (in details for existing synced events) */}
                  {isProviderSynced && !isNew && writableCalendars.length > 1 && (
                    <div className="event-field">
                      <label className="event-field-label">Calendar</label>
                      <select
                        value={selectedCalendarId}
                        onChange={(e) => handleCalendarChange(e.target.value)}
                        className="event-field-input"
                        disabled={isMovingCalendar}
                      >
                        {writableCalendars.map((cal) => (
                          <option key={cal.id} value={cal.id}>
                            {cal.name}{cal.primary ? ' (Primary)' : ''}
                          </option>
                        ))}
                      </select>
                      {isMovingCalendar && (
                        <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
                          Moving event...
                        </span>
                      )}
                    </div>
                  )}

                  {/* Recurrence — not shown for travel types */}
                  {eventType !== 'flight' && eventType !== 'train' && eventType !== 'hotel' && eventType !== 'restaurant' && (
                    <div className="event-field">
                      <label className="event-field-label">Repeat</label>
                      <select
                        value={recurrencePreset}
                        onChange={(e) => { markRecurrenceDirty(); setRecurrencePreset(e.target.value); }}
                        className="event-field-input"
                      >
                        <option value="none">Does not repeat</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly on {startDate ? new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }) : 'this day'}</option>
                        <option value="monthly">Monthly on the {startDate ? new Date(startDate + 'T00:00:00').getDate() : '?'}th</option>
                        <option value="yearly">Yearly</option>
                        <option value="weekdays">Every weekday (Mon-Fri)</option>
                        <option value="custom">Custom...</option>
                      </select>

                      {recurrencePreset === 'custom' && (
                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', background: 'var(--color-surface-muted, #f9fafb)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--color-text-subtle)' }}>Every</span>
                            <input type="number" min={1} max={99} value={customInterval}
                              onChange={(e) => { markRecurrenceDirty(); setCustomInterval(Math.max(1, parseInt(e.target.value) || 1)); }}
                              style={{ width: '56px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--color-border)', textAlign: 'center' }}
                            />
                            <select value={customFreq} onChange={(e) => { markRecurrenceDirty(); setCustomFreq(e.target.value as typeof customFreq); }}
                              style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                              <option value="DAILY">{customInterval > 1 ? 'days' : 'day'}</option>
                              <option value="WEEKLY">{customInterval > 1 ? 'weeks' : 'week'}</option>
                              <option value="MONTHLY">{customInterval > 1 ? 'months' : 'month'}</option>
                              <option value="YEARLY">{customInterval > 1 ? 'years' : 'year'}</option>
                            </select>
                          </div>
                          {customFreq === 'WEEKLY' && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {dayLabels.map((label, i) => (
                                <button key={i} type="button"
                                  onClick={() => { markRecurrenceDirty(); setCustomDays(prev => prev.includes(i) ? (prev.length > 1 ? prev.filter(d => d !== i) : prev) : [...prev, i]); }}
                                  style={{
                                    width: '32px', height: '32px', borderRadius: '50%', fontSize: '12px', fontWeight: 600,
                                    border: customDays.includes(i) ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                    background: customDays.includes(i) ? 'var(--color-primary)' : 'transparent',
                                    color: customDays.includes(i) ? 'white' : 'var(--color-text)', cursor: 'pointer',
                                  }}
                                >{label}</button>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', color: 'var(--color-text-subtle)' }}>Ends</span>
                            <select value={customEndType} onChange={(e) => { markRecurrenceDirty(); setCustomEndType(e.target.value as typeof customEndType); }}
                              style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                              <option value="never">Never</option>
                              <option value="count">After</option>
                              <option value="until">On date</option>
                            </select>
                            {customEndType === 'count' && (
                              <>
                                <input type="number" min={1} max={999} value={customCount}
                                  onChange={(e) => { markRecurrenceDirty(); setCustomCount(Math.max(1, parseInt(e.target.value) || 1)); }}
                                  style={{ width: '56px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--color-border)', textAlign: 'center' }}
                                />
                                <span style={{ fontSize: '13px', color: 'var(--color-text-subtle)' }}>occurrences</span>
                              </>
                            )}
                            {customEndType === 'until' && (
                              <input type="date" value={customUntil} onChange={(e) => { markRecurrenceDirty(); setCustomUntil(e.target.value); }} min={startDate}
                                style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--color-border)' }}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location — Places autocomplete (free text still allowed) */}
                  <div className="event-field">
                    <label className="event-field-label">Location</label>
                    <LocationAutocompleteField
                      value={location}
                      onChange={(text) => { setLocation(text); setLocationPlaceId(''); }}
                      onSelect={({ fullText, placeId }) => { setLocation(fullText); setLocationPlaceId(placeId); }}
                      placeholder="Add location"
                    />
                    {location.trim() && (
                      <a
                        href={buildMapUrl(location, locationPlaceId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="event-location-map-link"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        View on map
                      </a>
                    )}
                  </div>

                  {/* Attendees */}
                  <div className="event-field">
                    <label className="event-field-label">Attendees</label>
                    <AttendeeChips
                      attendees={attendees}
                      onChange={setAttendees}
                      sendInvitations={sendInvitations}
                      onSendInvitationsChange={setSendInvitations}
                      viewOnly={viewOnly}
                    />
                  </div>

                  {/* Video Conferencing */}
                  <div className="event-field">
                    <label className="event-field-label">Video call</label>
                    <VideoConferenceField
                      hangoutLink={event?.hangoutLink}
                      customConferenceUrl={customConferenceUrl}
                      createGoogleMeet={createGoogleMeet}
                      onCreateGoogleMeetChange={setCreateGoogleMeet}
                      onCustomConferenceUrlChange={setCustomConferenceUrl}
                      viewOnly={viewOnly}
                    />
                  </div>

                  {/* Description */}
                  <div className="event-field">
                    <label className="event-field-label">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add details"
                      rows={3}
                      className="event-field-input"
                      style={{ resize: 'none' }}
                    />
                  </div>

                  {/* Notes */}
                  <div className="event-field">
                    <label className="event-field-label">Notes</label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <input
                        type="text"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddNote();
                          }
                        }}
                        placeholder="Add a note..."
                        className="event-field-input"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="event-modal-save-btn"
                        style={{ flexShrink: 0 }}
                      >
                        Add
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 192, overflowY: 'auto' }}>
                      {notes.map((noteObj) => (
                        <div key={noteObj.id} className="event-note-item" style={{ margin: 0 }}>
                          {editingNoteId === noteObj.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <textarea
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                className="event-field-input"
                                rows={2}
                                autoFocus
                                style={{ resize: 'none' }}
                              />
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={handleCancelNoteEdit}
                                  style={{ padding: '4px 8px', fontSize: 12, color: 'var(--color-text-muted)', background: 'none', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleSaveNoteEdit}
                                  className="event-modal-save-btn"
                                  style={{ padding: '4px 8px', fontSize: 12 }}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="event-note-header">
                                <span className="event-note-meta">
                                  {noteObj.authorName || 'Unknown'} — {new Date(noteObj.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {new Date(noteObj.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                  {noteObj.updatedAt && noteObj.updatedAt !== noteObj.createdAt && ' (edited)'}
                                </span>
                                {noteObj.authorId === profile?.id && (
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button
                                      type="button"
                                      onClick={() => handleEditNote(noteObj)}
                                      style={{ padding: 4, color: 'var(--color-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                                      title="Edit note"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteNote(noteObj.id)}
                                      style={{ padding: 4, color: 'var(--color-text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                                      title="Delete note"
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p className="event-note-content">{noteObj.content}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══ Actions Footer ═══ */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 20px 20px 20px',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
                position: 'sticky',
                bottom: 0,
                background: 'var(--color-surface)',
                zIndex: 1,
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                {!isNew && event?.id && onDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this event?')) {
                        onDelete(event.id);
                        onClose();
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 32, height: 32, background: 'transparent',
                      border: 'none', borderRadius: 6, color: 'var(--color-text-faint)', cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-delete-bg)'; e.currentTarget.style.color = 'var(--color-action-delete-text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                )}
                {!isNew && event?.id && onArchive && (
                  <button
                    type="button"
                    onClick={() => { onArchive(event.id); onClose(); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 32, height: 32, background: 'transparent',
                      border: 'none', borderRadius: 6, color: 'var(--color-text-faint)', cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-archive-bg)'; e.currentTarget.style.color = 'var(--color-action-archive-text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 8v13H3V8" />
                      <path d="M1 3h22v5H1z" />
                      <path d="M10 12h4" />
                    </svg>
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
                    cursor: 'pointer', transition: 'all 0.2s',
                    background: 'var(--color-action-cancel-bg)',
                    border: '1px solid var(--color-action-cancel-border)',
                    color: 'var(--color-action-cancel-text)', minWidth: '80px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-cancel-bg-hover)'; e.currentTarget.style.borderColor = 'var(--color-action-cancel-border-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-action-cancel-bg)'; e.currentTarget.style.borderColor = 'var(--color-action-cancel-border)'; }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValid || isMovingCalendar}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 500,
                    cursor: (isValid && !isMovingCalendar) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    background: isValid ? 'var(--color-action-save-bg)' : '#d1d5db',
                    border: 'none',
                    color: 'var(--color-action-save-text)', minWidth: '80px',
                    opacity: isValid ? 1 : 0.5,
                  }}
                  onMouseEnter={(e) => { if (isValid) e.currentTarget.style.background = 'var(--color-action-save-bg-hover)'; }}
                  onMouseLeave={(e) => { if (isValid) e.currentTarget.style.background = 'var(--color-action-save-bg)'; }}
                >
                  {isNew ? 'Add Event' : 'Save'}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );

  const recurringMoveDialog = showRecurringMoveConfirm ? (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { e.stopPropagation(); cancelRecurringMove(); }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: '20px 24px',
          maxWidth: 400,
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#111827' }}>
          Move recurring series?
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--color-text-faint)', lineHeight: 1.5 }}>
          This will move all events in this series to{' '}
          <strong>{writableCalendars.find(c => c.id === pendingMoveCalendarId)?.name || 'the selected calendar'}</strong>.
          Continue?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={cancelRecurringMove}
            style={{
              padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
              background: '#f3f4f6', border: '1px solid #d1d5db', color: 'var(--color-text-muted)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmRecurringMove}
            style={{
              padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
              background: 'var(--color-primary, #2563eb)', border: 'none', color: 'white', cursor: 'pointer',
            }}
          >
            Move Series
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) {
    console.error('Modal portal root not found');
    return null;
  }
  return (
    <>
      {createPortal(modalContent, portalRoot)}
      {recurringMoveDialog && createPortal(recurringMoveDialog, portalRoot)}
      {createPortal(
        <UnsavedChangesGuardModal
          show={showUnsavedConfirmation}
          onCancel={() => setShowUnsavedConfirmation(false)}
          onDiscard={onClose}
          // requestSubmit fires the form's onSubmit (handleSubmit), which saves
          // and closes via onSave — same path as the Save button.
          onSave={() => eventFormRef.current?.requestSubmit()}
        />,
        portalRoot,
      )}
    </>
  );
}
